/* ═══════════════════════════════════════════════════════════════
   columnPrefs — pure resolver for the DataTable column-visibility/
   order ladder, per table:

       the user's own saved choice  →  the FIRM default  →  the
       code's out-of-box defaultHidden flags

   A firm default (published via admin_center.manage_firm_settings,
   stored in firm_prefs) BEATS a user preference that was saved
   BEFORE the default was published — so publishing a default
   actually changes what every colleague who never customised the
   table sees, including someone who toggled a column months ago.
   A user edit made AFTER the firm default was published wins for
   that one person. A legacy user pref with no timestamp (saved
   before this field existed, or never saved) counts as "before"
   any firm default.

   Pure, no I/O — DataTable supplies the raw values it already
   loaded from localStorage / Prefs / FirmPrefs. Unit-tested in
   columnPrefs.test.ts.
   ═══════════════════════════════════════════════════════════════ */

export interface FirmColumnDefault {
  hidden: string[];
  order: string[];
  setAt: string; // ISO timestamp
  setBy?: string;
}

export interface ResolveColumnPrefsInput {
  /** The user's own hidden-column set for this table, or null if they have
   *  never touched it on this account. */
  userHidden: string[] | null;
  /** ISO timestamp of the user's last hidden-column edit, or null — "no
   *  timestamp" covers both "never edited" and a legacy pref saved before
   *  this field existed; both count as "before" any firm default. */
  userHiddenAt: string | null;
  /** The user's own column order for this table, or null if untouched. */
  userOrder: string[] | null;
  userOrderAt: string | null;
  /** The firm-wide default an admin published for this table, or null. */
  firmDefault: FirmColumnDefault | null;
  /** The column keys the CODE marks `defaultHidden: true` right now. */
  codeDefaultHidden: string[];
  /** Every column key the code declares right now, in its declared order —
   *  the source of truth for "does this column still exist" and "is this
   *  column new since the default/pref in play was saved". */
  codeOrder: string[];
}

export interface ResolvedColumnPrefs {
  hidden: string[];
  order: string[];
}

function isAfter(candidate: string | null, reference: string): boolean {
  if (!candidate) return false; // no/legacy timestamp -> counts as "before"
  const c = Date.parse(candidate);
  const r = Date.parse(reference);
  if (Number.isNaN(c)) return false;
  if (Number.isNaN(r)) return true;
  return c > r;
}

function reconcileOrder(base: string[], codeOrder: string[]): string[] {
  const codeSet = new Set(codeOrder);
  const existing = base.filter((k) => codeSet.has(k));
  const existingSet = new Set(existing);
  const added = codeOrder.filter((k) => !existingSet.has(k));
  return [...existing, ...added];
}

export function resolveColumnPrefs(input: ResolveColumnPrefsInput): ResolvedColumnPrefs {
  const {
    userHidden,
    userHiddenAt,
    userOrder,
    userOrderAt,
    firmDefault,
    codeDefaultHidden,
    codeOrder,
  } = input;
  const codeSet = new Set(codeOrder);

  // ── Order ──
  const firmWinsOrder = !!firmDefault && !isAfter(userOrderAt, firmDefault.setAt);
  const orderBase = firmWinsOrder ? firmDefault!.order : userOrder ?? codeOrder;
  const order = reconcileOrder(orderBase, codeOrder);

  // ── Hidden ──
  const firmWinsHidden = !!firmDefault && !isAfter(userHiddenAt, firmDefault.setAt);
  let hiddenBase: string[];
  let known: Set<string>; // columns the winning source actually accounted for
  if (firmWinsHidden) {
    hiddenBase = firmDefault!.hidden;
    known = new Set(firmDefault!.order.length > 0 ? firmDefault!.order : codeOrder);
  } else if (userHidden !== null) {
    hiddenBase = userHidden;
    known = new Set(userOrder ?? codeOrder);
  } else {
    hiddenBase = codeDefaultHidden;
    known = new Set(codeOrder);
  }
  const hiddenSet = new Set(hiddenBase.filter((k) => codeSet.has(k)));
  // A column the code introduced AFTER the winning source was saved is
  // unknown to it — such a column falls back to the code's own
  // defaultHidden flag, not "always visible by omission".
  for (const key of codeOrder) {
    if (!known.has(key) && codeDefaultHidden.includes(key)) hiddenSet.add(key);
  }

  return { hidden: [...hiddenSet], order };
}
